import { DialogRef } from '@angular/cdk/dialog';
import { Component, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { RecaptchaFormsModule, RecaptchaModule } from 'ng-recaptcha-2';
import { UtilityService } from '../core/utility.service';

@Component({
    standalone: true,
    selector: 'app-subscribe-dialog',
    imports: [FormsModule, RecaptchaModule, RecaptchaFormsModule],
    template: `
        <div class="subscribe-dialog">
            <button class="subscribe-close" type="button" (click)="dialogRef.close()" aria-label="Close">×</button>
            @if (!submitted) {
                <h3 class="subscribe-title">Sign up for all of our latest product news and special offers!</h3>
                <form #form="ngForm">
                    <div class="subscribe-field">
                        <input
                            #name="ngModel"
                            [(ngModel)]="subscribeName"
                            placeholder="Your Name"
                            type="text"
                            id="subscribeName"
                            name="subscribeName"
                            required
                            maxlength="100"
                        />
                        @if (name.errors?.['required'] && (name.touched || name.dirty)) {
                            <span class="field-error">Name is required</span>
                        }
                    </div>
                    <div class="subscribe-field">
                        <input
                            #email="ngModel"
                            [(ngModel)]="subscribeEmail"
                            placeholder="Your Email"
                            type="email"
                            id="subscribeEmail"
                            name="subscribeEmail"
                            required
                            maxlength="100"
                        />
                        @if (email.errors?.['required'] && (email.touched || email.dirty)) {
                            <span class="field-error">Email is required</span>
                        }
                    </div>
                    <div class="subscribe-field privacy-consent">
                        <input
                            #consent="ngModel"
                            [(ngModel)]="agreedToPolicy"
                            type="checkbox"
                            id="privacyConsent"
                            name="privacyConsent"
                            required
                        />
                        <label for="privacyConsent">
                            By submitting, you agree to our <a href="/privacy-policy" target="_blank" class="link">Privacy Policy</a> and consent to receive updates via email and SMS.
                        </label>
                    </div>
                    <div class="subscribe-field">
                        <re-captcha
                            id="recaptcha"
                            name="recaptcha"
                            #recaptcha="ngModel"
                            [(ngModel)]="token"
                            required
                        ></re-captcha>
                        @if (recaptcha.invalid && (recaptcha.dirty || recaptcha.touched) && recaptcha.errors?.['required']) {
                            <span class="field-error">Recaptcha is required.</span>
                        }
                    </div>
                    <div class="subscribe-actions">
                        <button type="button" class="subscribe-btn" (click)="submit(form)">Subscribe</button>
                    </div>
                </form>
            } @else {
                <h3 class="subscribe-title">Thank You!</h3>
                <p style="color:#555; margin-bottom: 24px;">You've been subscribed to our latest product news and special offers.</p>
                <div class="subscribe-actions">
                    <button type="button" class="subscribe-btn" (click)="dialogRef.close()">Close</button>
                </div>
            }
        </div>
    `,
    styles: `
        .subscribe-dialog {
            position: relative;
            width: 100%;
            padding: 60px 60px 50px;
            background: #fff;
            box-sizing: border-box;

            @media (max-width: 600px) {
                padding: 48px 24px 36px;
            }
        }

        .subscribe-close {
            position: absolute;
            top: 12px;
            right: 16px;
            background: none;
            border: none;
            font-size: 24px;
            line-height: 1;
            color: #333;
            cursor: pointer;
            padding: 4px 8px;
        }

        .subscribe-close:hover {
            color: #000;
        }

        .subscribe-title {
            margin: 0 0 28px;
            font-size: 20px;
            font-weight: 500;
            color: #333;
            text-align: center;
            line-height: 1.4;
            text-transform: none;
            font-family: Eurostile, Arial, Helvetica, sans-serif;
        }

        .subscribe-field {
            margin-bottom: 16px;
        }

        .subscribe-field input[type="text"],
        .subscribe-field input[type="email"] {
            width: 100%;
            padding: 10px 14px;
            box-sizing: border-box;
            border: 1px solid #ccc;
            border-radius: 2px;
            font-size: 14px;
            color: #444;
            background: #fff;
        }

        .subscribe-field input[type="text"]:focus,
        .subscribe-field input[type="email"]:focus {
            outline: none;
            border-color: #999;
        }

        .privacy-consent {
            display: flex;
            align-items: flex-start;
            gap: 8px;
            font-size: 13px;
            color: #555;
            line-height: 1.5;
        }

        .privacy-consent input[type="checkbox"] {
            margin-top: 3px;
            flex-shrink: 0;
        }

        .field-error {
            display: block;
            margin-top: 4px;
            color: #c03;
            font-size: 12px;
        }

        .subscribe-actions {
            margin-top: 22px;
        }

        .subscribe-btn {
            padding: 10px 22px;
            font-size: 14px;
            font-weight: 500;
            background-color: var(--main-bg-color);
            color: #fff;
            border: none;
            border-radius: 2px;
            cursor: pointer;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .subscribe-btn:hover {
            opacity: 0.9;
        }
    `
})
export class SubscribeDialogComponent {
    public dialogRef: DialogRef = inject(DialogRef);
    utilityService = inject(UtilityService);

    subscribeName = '';
    subscribeEmail = '';
    agreedToPolicy = false;
    token: string | undefined;
    submitted = false;

    async submit(form: NgForm) {
        Object.keys(form.controls).forEach((key) => form.controls[key].markAsTouched());

        if (form.invalid) return;

        await this.utilityService.submitSubscribe(this.subscribeName, this.subscribeEmail);
        this.submitted = true;
    }
}
